import { defineStore } from "pinia";
import { ref, computed } from "vue";

import { Extra, ExtraItem } from "@/app/domain/entities/extra";
import { usePartitionStore } from "./partition.store";
import { unwrapRef } from "@/utils/reactive";
import { IPricing, Pricing } from "@/app/domain";

export const useExtraStore = defineStore(
    "extra-store",
    () => {
        const partitionsStore = usePartitionStore();
        const activePartition = computed(
            () => partitionsStore.activePartitions,
        );

        const activeExtra = ref<Extra | null>(null);
        const activeExtraItems = ref<ExtraItem[]>([]);
        const discountedExtraTotal = ref<IPricing>(new Pricing(0, 0, 0));
        const extraTotal = ref<IPricing>(new Pricing(0, 0, 0));

        const hasActiveExtra = computed(() => Boolean(partitionsStore.activePartitions?.getExtra()));

        const $restoreExtra = () => {
            setActiveExtra(activePartition.value);
        };

        const setActiveExtra = (partition?: any) => {
            if (!partition) return;

            activeExtra.value = partition?.getExtra() || null;
            activeExtraItems.value = partition?.getExtra().components as ExtraItem[];
        }

        const $reset = () => {
            activeExtra.value = null;
            activeExtraItems.value = [];
            extraTotal.value = new Pricing(0, 0, 0);
            discountedExtraTotal.value = new Pricing(0, 0, 0);
        };

        const addExtra = () => {
            if (!activePartition.value) {
                return;
            }

            const extra = new Extra({
                qty: 1,
            });

            activePartition.value.addComponent(extra);
            activeExtra.value = extra;
        };

        const addExtraItem = (discount: boolean = false) => {
            if (!activeExtra.value) {
                return;
            }

            const extraItem = new ExtraItem({
                qty: 1,
                discount: discount,
            });

            activeExtra.value.addComponent(extraItem);
            activeExtraItems.value.push(extraItem);
        };

        const removeExtraItem = (extraItem: ExtraItem) => {
            if (!activeExtra.value) {
                return;
            }

            const newComponents = activeExtra.value.removeComponent(
                unwrapRef(extraItem.id),
                unwrapRef(extraItem.type).value,
            ) as ExtraItem[];

            activeExtra.value?.update({
                components: newComponents,
            })
            activeExtraItems.value = newComponents;
        };

        return {
            activeExtra,
            activeExtraItems,
            hasActiveExtra,
            $reset,
            $restoreExtra,
            addExtra,
            addExtraItem,
            removeExtraItem,
            discountedExtraTotal,
            extraTotal,
            setActiveExtra
        };
    },
    { persist: true },
);
